/**
 * Plan gate for demographics/census overlays (M9).
 *
 * The overlay capability is gated by THREE co-requisites:
 *   (a) a white-label BUILD-GO flag (`brand.demographicsOverlays`) — OFF by
 *       default so the wedge ships without the overlay engine exposed;
 *   (b) a deploy-time render ENGINE actually bound (`OverlayEngine`) — the
 *       choropleth is drawn by that engine, undefined in the editor/viewer wedge;
 *       and
 *   (c) the M7 ENTITLEMENT check (`demographics_overlays` capability) at open time.
 *
 * (b) is the co-gate: enabling the flag WITHOUT binding an engine would let the
 * authoring panel persist an `overlay` block into the spec that renders BLANK
 * everywhere (no engine to resolve the choropleth) with no error. So the gate
 * reports `present:false` (panel hidden) unless an engine is bound — the flag
 * and the render path can never drift apart.
 *
 * The gate READS the M7 contract — it does NOT duplicate gate logic. The
 * entitlement input is TIER-derived (`Entitlement.isPaid`), never a
 * consumption/render-call count, exactly like the M8 analysis gate (`@/lib/analysis/gate`).
 * Entitled → the overlay renders; Free → a locked-state seam (the upsell UI is
 * M9-B). Pure + synchronous: resolved brand flag + engine-availability +
 * entitlement flow in as props.
 */

import { capabilityGate, type CapabilityBlock, type TierEntitlement } from "@/lib/billing/tierEntitlement";

/** Tier-derived entitlement for the overlay capability (the shared gate primitive). */
export type OverlayEntitlement = TierEntitlement;

/** The shared block reasons plus the overlay-only `no_engine` co-gate rung. */
export type OverlayBlock = CapabilityBlock | "no_engine";

export interface OverlayGate {
  /** Build-go: is the overlay capability present in the build at all? */
  present: boolean;
  /** present AND entitled — the overlay renders (vs the locked upsell seam). */
  openable: boolean;
  /** Why it can't render (or null when it can). */
  blockedReason: OverlayBlock;
  /** present but NOT entitled — the Free-tier locked seam should show (upsell is M9-B). */
  locked: boolean;
}

/** Tier-derived entitlement decision — the ONE place overlays read "may use". */
export function isOverlayEntitled(entitlement: OverlayEntitlement): boolean {
  return entitlement.isPaid;
}

/**
 * Gate the overlay capability given its build-go flag, whether a render engine
 * is actually bound, and the account's entitlement. `demographicsOverlays` is
 * the resolved `brand.demographicsOverlays` value (default off); `engineBound`
 * is true only when a deploy-time `OverlayEngine` is wired (the choropleth's
 * render source). Both must hold before entitlement is even consulted — a
 * flag-on / no-engine deployment reports `present:false` so the authoring panel
 * is hidden (no spec-persisting-but-blank overlays). The flag-off default is
 * unchanged.
 */
export function overlayGate(
  demographicsOverlays: boolean,
  entitlement: OverlayEntitlement,
  engineBound: boolean,
): OverlayGate {
  // The two build-side co-requisites are layered ON TOP of the shared primitive:
  // flag off, then no bound engine, both report present:false. Only once both
  // hold does the shared tier gate decide openable vs the locked upsell seam.
  if (!demographicsOverlays) {
    return { present: false, openable: false, blockedReason: "build_off", locked: false };
  }
  if (!engineBound) {
    return { present: false, openable: false, blockedReason: "no_engine", locked: false };
  }
  return capabilityGate(true, entitlement);
}
